import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { utils } from "framework7";
import { f7 } from "framework7-vue";
import { openAdaptiveModal, closeAdaptiveModal } from "./useAdaptiveModal.js";
import { Capacitor } from "@capacitor/core";
import { VoiceRecorder } from "@independo/capacitor-voice-recorder";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FilePicker } from "@capawesome/capacitor-file-picker";
import { FileOpener } from "@capawesome-team/capacitor-file-opener";
import { PhotoEditor } from "@capawesome/capacitor-photo-editor";
import { Browser } from "@capacitor/browser";
import { useAuthStore } from "../pinia-stores/auth.js";
import { useCamera } from "./useCamera.js";
import i18n from "../i18n.js";

export function useAttachDocs({ isTablet, type } = {}) {
  const authStore = useAuthStore();
  const t = i18n.global.t;

  const imGattachments = ref([]);
  const attachment = ref("");
  const content = ref("");
  const msgType = ref("text");
  const forms = ref("");
  const inputFile = ref("");
  const image = ref(null);
  const docsUrl = ref([]);
  const documentFile = ref("");
  const actionGridOpened = ref(false);
  const selectedDocument = ref("");
  const docToView = ref("");
  const videoUrl = ref("");
  const videoName = ref("");
  const isDragging = ref(false);
  const files = ref([]);
  const storedFileNames = ref([]);
  const pickedFiles = ref([]);
  const pickedFilesWithPath = ref([]);
  const base64String = ref("");
  const mediaRecorder = ref(null);
  const actionsAttachFiles = ref(null);
  const isRecording = ref(false);

  const documentsUrl = computed(() => authStore.documentsUrl);
  const documentData = computed(() => authStore.document);
  const videoService = computed(() => authStore.videoService);
  const videosUrl = computed(() => authStore.videosUrl);

  const photoList = computed(() => {
    const photos = [];
    for (const photo of documentsUrl.value) {
      if (photo.fileType === "image") {
        photos.push({
          url: photo.url,
          caption: photo.fileName,
          id: photo.fileId,
        });
      }
      if (photo.fileType === "video") {
        photos.push({
          html: `<video src="${photo.url}" controls style="width: 100%; height: calc(100vh - var(--f7-navbar-height))"> </video>`,
          caption: photo.fileName,
          id: photo.fileId,
        });
      }
      if (photo.fileType === "audio") {
        photos.push({
          html: `<audio src="${photo.url}" controls > </audio>`,
          caption: photo.fileName,
          id: photo.fileId,
        });
      }
      if (photo.contentType === "application/pdf") {
        photos.push({
          html: `<iframe src="${photo.url}" width="100%" height="100%"></iframe>`,
          caption: photo.fileName,
          id: photo.fileId,
        });
      }
    }
    console.log("photos", photos);
    return photos;
  });

  onMounted(() => {
    const messagePage = document.getElementsByClassName("tamtamMessagePage");
    if (messagePage.length > 0 && Capacitor.isNativePlatform()) {
      VoiceRecorder.canDeviceVoiceRecord().then((result) => {
        console.log("canDeviceVoiceRecord", result);
        if (!result.value) {
          f7.$(".recordBtn").hide();
        }
      });
    }
  });

  onBeforeUnmount(() => {
    for (const doc of authStore.documentsUrl) {
      if (doc?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(doc.url);
      }
    }
    authStore.clearDocumentsUrl();
  });

  function startAudioRecording() {
    if (isRecording.value) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      checkRecordingCapability();
      checkAudioRecordingPermission();
      VoiceRecorder.startRecording();
    }
    navigator.mediaDevices
      .getUserMedia({
        audio: true,
      })
      .then((stream) => {
        const recorder = new MediaRecorder(stream);
        recorder.start();
        const audioChunks = [];
        recorder.addEventListener("dataavailable", (event) => {
          audioChunks.push(event.data);
        });
        console.log("mediaRecorder audioChunks", audioChunks);
        recorder.addEventListener("stop", () => {
          const audioBlob = new Blob(audioChunks);
          const file = new File([audioBlob], `audio-${Date.now()}.mp3`, {
            type: "audio/mp3",
          });
          setAttachment({ file });
        });
        mediaRecorder.value = recorder;
      });
    isRecording.value = true;
  }

  function checkAudioRecordingPermission() {
    VoiceRecorder.hasAudioRecordingPermission().then((result) => {
      if (result.value) {
        return true;
      }
      VoiceRecorder.requestAudioRecordingPermission();
    });
  }

  function checkRecordingCapability() {
    VoiceRecorder.canDeviceVoiceRecord().then((result) => {
      return result.value ? true : false;
    });
  }

  async function stopAudioRecording() {
    if (!isRecording.value) {
      return;
    }
    isRecording.value = false;
    if (Capacitor.isNativePlatform()) {
      const result = await VoiceRecorder.stopRecording();
      if (!result?.value) {
        return;
      }
      const audioRecord = result.value;
      console.log("audioRecord", audioRecord);
      const base64Audio = audioRecord.recordDataBase64;
      const mime_type = audioRecord.mimeType.includes(";")
        ? audioRecord.mimeType.split(";")[0]
        : audioRecord.mimeType;
      const fileName = `${mime_type.split("/")[0]}-${Date.now()}.${
        mime_type.split("/")[1]
      }`;

      await writeToFileSystem(fileName, base64Audio);

      const response = await fetch(`data:${mime_type};base64,${base64Audio}`);
      const blob = await response.blob();
      const file = new File([blob], fileName, { type: mime_type });

      setAttachment({ file });
    }
    mediaRecorder.value.stop();
  }

  async function writeToFileSystem(fileName, data) {
    await Filesystem.writeFile({
      path: fileName,
      data: data,
      directory: Directory.Data,
    });
  }

  async function fileSystemLoadFiles() {
    await Filesystem.readdir({
      path: "",
      directory: Directory.Data,
    }).then((result) => {
      const files = result.files;
      console.log("files", files);
      storedFileNames.value = files;
    });
  }

  async function getFileSystemFile(fileName) {
    const file = await Filesystem.readFile({
      path: fileName,
      directory: Directory.Data,
    });
    return file.data;
  }

  function deleteFileSystemFile(fileName) {
    Filesystem.deleteFile({
      path: fileName,
      directory: Directory.Data,
    });
  }

  async function downloadFile(fileName, url) {
    if (!fileName) {
      fileName = url.substring(url.lastIndexOf("/") + 1);
      console.log("fileName", fileName);
    }
    await Filesystem.downloadFile({
      url,
      path: fileName,
    });
  }

  function checkFinePickerPermission() {
    if (Capacitor.getPlatform() === "ios") {
      FilePicker.hasReadPermission().then((result) => {
        if (result.value) {
          return true;
        }
        FilePicker.requestReadPermission();
      });
    }
  }

  async function pickAnyFile() {
    checkFinePickerPermission();
    try {
      const result = await FilePicker.pickFiles();
      console.log("pickedFiles result", result);
      if (!result.files) {
        return;
      }
      pickedFiles.value = result.files;
      console.log("pickedFiles", pickedFiles.value);
      const files = result.files;
      processFilePickerFiles(files);
    } catch (error) {
      console.error("Error picking files:", error);
    }
  }

  async function pickVideos() {
    checkFinePickerPermission();
    try {
      const result = await FilePicker.pickVideos();
      if (!result.files) {
        return;
      }
      pickedFiles.value = result.files;
      console.log("pickedFiles", pickedFiles.value);
      const files = result.files;
      processFilePickerFiles(files);
    } catch (error) {
      console.error("Error picking videos:", error);
    }
  }

  async function pickImages() {
    checkFinePickerPermission();
    try {
      const result = await FilePicker.pickImages();
      if (!result.files) {
        return;
      }
      console.log("result", result);
      pickedFiles.value = result.files;
      const files = result.files;
      processFilePickerFiles(files);
    } catch (error) {
      console.error("Error picking images:", error);
    }
  }

  async function pickMedia() {
    checkFinePickerPermission();
    try {
      const result = await FilePicker.pickMedia();
      if (!result.files) {
        return;
      }
      console.log("result", result);
      pickedFiles.value = result.files;
      const files = result.files;
      processFilePickerFiles(files);
    } catch (error) {
      console.error("Error picking media:", error);
    }
  }

  async function pickDirectory() {
    checkFinePickerPermission();
    try {
      const result = await FilePicker.pickDirectory();
      if (!result.files) {
        return;
      }
      console.log("result", result);
      pickedFiles.value = result.files;
      const files = result.files;
      processFilePickerFiles(files);
    } catch (error) {
      console.error("Error picking directory:", error);
    }
  }

  async function processFilePickerFiles(pickedFilesArg) {
    for (const file of pickedFilesArg) {
      if (file.blob) {
        const rawFile = new File([file.blob], file.name, {
          type: file.mimeType ? file.mimeType : file.type,
        });
        setAttachment({ file: rawFile });
      } else if (file.path) {
        try {
          const fileData = await Filesystem.readFile({
            path: file.path,
          });
          console.log("fileData", fileData);
          if (!fileData.data) {
            console.error("No data found for file:", file.path);
            continue;
          }
          console.log("fileData.data", fileData.data);
          const mimeType = file.mimeType
            ? file.mimeType
            : file.type || "application/octet-stream";
          const response = await fetch(
            `data:${mimeType};base64,${fileData.data}`
          );
          const blob = await response.blob();
          console.log("blob", blob);
          const rawFile = new File([blob], file.name, { type: mimeType });
          setAttachment({ file: rawFile });
        } catch (err) {
          console.error("Failed to read file from path:", file.path, err);
        }
      }
    }
  }

  async function getPickedFilesPath() {
    pickedFilesWithPath.value = await Promise.all(
      pickedFiles.value.map(async (file) => {
        let path = "";
        if (Capacitor.isNativePlatform()) {
          path = file.path || "";
        } else if (file.blob) {
          path = URL.createObjectURL(file.blob);
        }
        return {
          mimeType: file.mimeType ? file.mimeType : file.type,
          path,
          blob: file.blob,
        };
      })
    );
    console.log("pickedFilesWithPath", pickedFilesWithPath.value);
  }

  async function openFileWithFileOpener(file) {
    try {
      if (Capacitor.isNativePlatform() && file.path) {
        await FileOpener.openFile({
          path: file.path,
          mimeType: file.type ? file.type : file.mimeType,
        });
      } else if (file.url.startsWith("blob:")) {
        await FileOpener.openFile({
          path: "",
          mimeType: file.type ? file.type : file.mimeType,
          blob: file.blob,
        });
      } else if (file.url.startsWith("http")) {
        await Browser.open({ url: file.url });
      } else {
        throw new Error("No valid file path or blob provided.");
      }
    } catch (error) {
      console.error("Failed to open file:", error);
      f7.dialog.alert(t("common.unableToOpenFile"));
    }
  }

  function setDocumentsUrl({ blob, file = blob }) {
    const docsToAttach = {
      fileName: file.name,
      contentType: file.type,
      alt: file.name,
      fileId: `${utils.id("xxxx-xxxx-xxxx-xxxx")}/${file.name}`,
      size: file.size,
      fileType: file.type.split("/", 1)[0],
      url: window.URL.createObjectURL(file),
      file,
    };
    useAuthStore().setDocumentsUrl(docsToAttach);
  }

  function readFileBlob(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onerror = console.error;
      reader.onload = async (event) =>
        resolve(await (await fetch(event.target.result)).blob());
      reader.readAsDataURL(file);
    });
  }

  function blobTobase64(blob) {
    const reader = new FileReader();
    reader.onerror = console.error;
    reader.onloadend = () => {
      reader.result;
      console.log("base64", reader.result.split(",")[1]);
    };
    reader.readAsDataURL(blob);
  }

  function setAttachment({ blob, file = blob }) {
    console.log("file", file);
    const docsToAttach = {
      fileName: file.name,
      contentType: file.type,
      alt: file.name,
      fileId: utils.id("xxxx-xxxx-xxxx-xxxx") + "/" + file.name,
      size: file.size,
      fileType: file.type.split("/")[0],
      url: window.URL.createObjectURL(file),
      file,
    };
    content.value = {
      body: file.name,
      msgtype: attachment.value.msgtype,
      mimetype: attachment.value.mimetype,
      filename: file.name,
    };
    console.log("attachment", docsToAttach);
    setTimeout(() => {
      useAuthStore().setDocumentsUrl(docsToAttach);
    }, 500);
  }

  function getMsgType(fileType) {
    const type = fileType.split("/", 1)[0];
    switch (type) {
      case "image":
        return "image";
      case "audio":
        return "audio";
      case "video":
        return "video";
      default:
        return "file";
    }
  }

  function resetAttachment() {
    if (!attachment.value) return attachment.value == undefined;
    window.URL.revokeObjectURL(attachment.value.file);
    content.value = {
      body: attachment.value
        ? content.value.body.replace(attachment.value.name, "")
        : "",
      msgtype: "text",
    };
    attachment.value = undefined;
    useAuthStore().setDocumentsUrl([]);
  }

  async function onPaste(event) {
    const data = event.clipboardData || event.originalEvent.clipboardData;
    if (!data || !data.items) return;
    for (const item of Array.from(data.items)) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) createFile([file]);
        return;
      }
    }
  }

  function onChange(fileInputRef) {
    const input =
      fileInputRef && fileInputRef.target ? fileInputRef.target : fileInputRef;
    if (!input || !input.files || input.files.length === 0) return;
    createFile(Array.from(input.files));
    input.value = null;
  }

  function dragover(e) {
    e.preventDefault();
    isDragging.value = true;
  }

  function dragleave(e) {
    if (!e.relatedTarget || !e.currentTarget || !e.currentTarget.contains(e.relatedTarget)) {
      isDragging.value = false;
    }
  }

  function drop(e) {
    e.preventDefault();
    isDragging.value = false;
    const fileList = e.dataTransfer && e.dataTransfer.files;
    if (!fileList || fileList.length === 0) return;
    createFile(Array.from(fileList));
  }

  function attachFileOpenActions({ takePhoto, openPreviewCamera } = {}) {
    const camera = useCamera();
    const current_page = f7.views.main.router.currentRoute.name;
    const takePhotoAction = takePhoto || camera.takePhoto;
    const openPreviewCameraAction = openPreviewCamera || camera.openPreviewCamera;
    const actionButtons = [
      {
        text: t("attachdocs.selectAPhoto"),
        icon: `<i class="f7-icons size-70 if-not-md color-lightblue">photo_on_rectangle</i><i class="material-icons size-70 md-only color-lightblue">image</i>`,
        color: "lightblue",
        strong: true,
        onClick() {
          pickImages();
          f7.actions.close();
        },
      },
      {
        text: t("attachdocs.takeAPhoto"),
        icon: `<i class="f7-icons if-not-md color-pink">camera</i><i class="material-icons md-only color-pink">photo_camera</i>`,
        color: "pink",
        strong: true,
        onClick() {
          if (takePhotoAction) takePhotoAction();
          f7.actions.close();
        },
      },
      {
        text: t("attachdocs.selectAVideo"),
        icon: `<i class="f7-icons if-not-md color-lime">play_rectangle</i><i class="material-icons md-only color-lime">video_library</i>`,
        color: "lime",
        strong: true,
        onClick() {
          pickVideos();
          f7.actions.close();
        },
      },
      {
        text: t("attachdocs.recordAVideo"),
        icon: `<i class="f7-icons if-not-md color-purple">videocam</i><i class="material-icons md-only color-purple">videocam</i>`,
        color: "purple",
        strong: true,
        onClick() {
          if (Capacitor.isNativePlatform()) {
            camera.recordVideoNative();
          } else if (openPreviewCameraAction) {
            openPreviewCameraAction();
          }
          f7.actions.close();
        },
      },
      {
        text: t("attachdocs.documents"),
        icon: `<i class="f7-icons if-not-md color-deeporange">doc_text</i><i class="material-icons md-only color-deeporange">description</i>`,
        color: "deeporange",
        strong: true,
        onClick() {
          pickAnyFile();
          f7.actions.close();
        },
      },
      {
        text: t("attachdocs.selectAnAudio"),
        icon: `<i class="f7-icons if-not-md color-teal">music_albums</i><i class="material-icons md-only color-teal">library_music</i>`,
        color: "teal",
        strong: true,
        onClick() {
          pickMedia();
          f7.actions.close();
        },
      },
    ];

    if (
      current_page === "tamtam_detail_page" ||
      current_page === "tamtam_main_page" ||
      current_page === "shop_detail" ||
      current_page === "tontine_detail" ||
      current_page === "wallet_detail" ||
      current_page === "savings_group_detail" ||
      current_page === "association_detail" ||
      current_page === "groupBuy_detail"
    ) {
      actionButtons.push({
        text: t("post.pollPost"),
        icon: `<i class="f7-icons if-not-md color-blue">chart_bar_square</i><i class="material-icons md-only color-blue">ballot</i>`,
        color: "blue",
        strong: true,
        onClick() {
          openAdaptiveModal(".createNewPoll", true);
          f7.actions.close();
        },
      });
    }

    if (f7.actions.opened) {
      f7.actions.close();
      actionsAttachFiles.value.close();
    }
    if (!actionsAttachFiles.value) {
      actionsAttachFiles.value = f7.actions.create({
        convertToPopover: true,
        closeOnEscape: true,
        closeByOutsideClick: true,
        backdrop: false,
        grid: isTablet ? false : true,
        buttons: actionButtons,
        targetEl: document.querySelector(".attachFileButton"),
      });
    }
    actionsAttachFiles.value.open();
  }

  function createFile(files) {
    for (const element of files) {
      const reader = new FileReader();
      if (element) {
        reader.readAsDataURL(element);
      }
      reader.onload = () => {
        const docsToAttach = {
          fileName: element.name,
          contentType: element.type,
          alt: element.name,
          fileId: `${utils.id("xxxx-xxxx-xxxx-xxxx")}/${element.name}`,
          size: element.size,
          fileType: element.type.split("/", 1)[0],
          url: window.URL.createObjectURL(element),
          file: element,
          base64Url: reader.result,
        };
        console.log("docsToAttach", docsToAttach);
        useAuthStore().setDocumentsUrl(docsToAttach);
      };
    }
  }

  function uploadFile(file) {
    if (!file) return;
    createFile([file]);
  }

  function removeDoc(index) {
    const doc = documentsUrl.value[index];
    useAuthStore().unSetDocumentsUrl(doc);
  }

  function deleteAttachment(image, composableDocumentsUrl, imGattachments) {
    if (!image) return;
    const docs = composableDocumentsUrl || documentsUrl;
    const index = docs.value ? docs.value.indexOf(image) : -1;
    if (index > -1) docs.value.splice(index, 1);
    if (imGattachments && imGattachments.value) {
      const index0 = imGattachments.value.indexOf(image);
      if (index0 > -1) imGattachments.value.splice(index0, 1);
    }
    if (image.url && image.url.startsWith("blob:")) {
      window.URL.revokeObjectURL(image.url);
    }
  }

  function editImage(image) {
    if (
      Capacitor &&
      Capacitor.isPluginAvailable("PhotoEditor") &&
      Capacitor.getPlatform() === "android"
    ) {
      editPhoto(image.file);
    }
    f7.preloader.show("multi");
    useAuthStore().selectDocument(image);
    if (documentData.value !== null) {
      openAdaptiveModal(".editImage");
    }
    f7.preloader.hide();
  }

  async function editPhoto(file) {
    if (!(file instanceof File)) {
      return;
    }
    const fileUrl = URL.createObjectURL(file);
    const result = await PhotoEditor.editPhoto({ path: fileUrl });

    if (result && result.base64) {
      const mimeType = file.type || "image/png";
      const byteString = atob(result.base64.split(",")[1] || result.base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const editedBlob = new Blob([ab], { type: mimeType });
      const editedFile = new File([editedBlob], file.name, {
        type: mimeType,
      });
      URL.revokeObjectURL(fileUrl);
      base64String.value = `data:${mimeType};base64,${
        result.base64.split(",")[1] || result.base64
      }`;
      setAttachment({ file: editedFile });
    } else {
      URL.revokeObjectURL(fileUrl);
      setAttachment({ file });
    }
  }

  function viewOtherFiles(currentImage) {
    console.log("currentImage", currentImage);
    useAuthStore().selectDocument(currentImage);
    openAdaptiveModal(".viewDocs");
  }

  function openDocument(currentImage) {
    const index = documentsUrl.value.findIndex(
      (item) => item.fileId === currentImage?.fileId
    );
    const photos = documentsUrl.value
      .map((item) => {
        if (item.fileType === "image") {
          return { url: item.url, caption: item.fileName, id: item.fileId };
        }
        if (item.fileType === "video") {
          return {
            html: `<video src="${item.url}" controls playsinline style="width: 100%; height: calc(100vh - var(--f7-navbar-height))"></video>`,
            caption: item.fileName,
            id: item.fileId,
          };
        }
        if (item.fileType === "audio") {
          return {
            html: `<audio src="${item.url}" controls style="width:100%;outline:none"></audio>`,
            caption: item.fileName,
            id: item.fileId,
          };
        }
        if (item.contentType === "application/pdf") {
          return {
            html: `<iframe src="${item.url}" width="100%" height="100%"></iframe>`,
            caption: item.fileName,
            id: item.fileId,
          };
        }
        return null;
      })
      .filter(Boolean);
    const photoBrowser = f7.photoBrowser.create({
      photos,
      initialSlide: index >= 0 ? index : 0,
      type: "popup",
    });
    photoBrowser.open();
  }

  function getFileMimeType(file) {
    if (
      (file &&
        file.contentType &&
        file.contentType.includes("spreadsheet")) ||
      (file && file.contentType && file.contentType.includes("excel"))
    ) {
      return {
        icon: generateIconHtml(
          "green",
          "rectangle_split_3x3",
          "table_view"
        ),
      };
    } else if (
      (file && file.contentType && file.contentType.includes("document")) ||
      (file && file.contentType && file.contentType.includes("word"))
    ) {
      return {
        icon: generateIconHtml("blue", "doc", "description"),
      };
    } else if (
      (file &&
        file.contentType &&
        file.contentType.includes("presentation")) ||
      (file && file.contentType && file.contentType.includes("powerpoint"))
    ) {
      return {
        icon: generateIconHtml(
          "orange",
          "uiwindow_split_2x1",
          "summarize"
        ),
      };
    } else if (
      file &&
      file.contentType &&
      file.contentType === "application/pdf"
    ) {
      return {
        icon: generateIconHtml(
          "deeporange",
          "doc_on_doc",
          "picture_as_pdf"
        ),
      };
    } else {
      return {
        icon: generateIconHtml("gray", "paperclip", "attachment"),
      };
    }
  }

  function generateIconHtml(color, f7Icon, materialIcon) {
    return `<i class="f7-icons if-not-md color-${color} size-70">${f7Icon}</i><i class="material-icons md-only color-${color} size-70">${materialIcon}</i>`;
  }

  function addVideoUrls(serviceType, vidType) {
    console.log(vidType);
    useAuthStore().setVideoServiceType({
      id: serviceType.id,
      type: vidType,
    });
    openAdaptiveModal(".addNewVideo");
  }

  function addVideo() {
    if (videoUrl.value !== "") {
      authStore.setVideosUrl([...(videosUrl.value || []), {
        url: videoUrl.value,
        name: videoName.value,
      }]);
      videoUrl.value = "";
      videoName.value = "";
    }
    console.log(videosUrl.value);
  }

  function removeVideo(index) {
    const updated = [...videosUrl.value];
    updated.splice(index, 1);
    authStore.setVideosUrl(updated);
  }

  async function saveVideos() {
    if (!(videosUrl.value && videosUrl.value.length > 0)) {
      return;
    }
    await useAuthStore().setVideoServiceType("");
    await useAuthStore().setVideosUrl("");
    closeAdaptiveModal(".addNewVideo");
  }

  return {
    imGattachments,
    attachment,
    content,
    msgType,
    forms,
    inputFile,
    image,
    docsUrl,
    documentFile,
    actionGridOpened,
    selectedDocument,
    docToView,
    videoUrl,
    videoName,
    isDragging,
    files,
    storedFileNames,
    pickedFiles,
    pickedFilesWithPath,
    base64String,
    mediaRecorder,
    actionsAttachFiles,
    isRecording,
    documentsUrl,
    documentData,
    photoList,
    startAudioRecording,
    checkAudioRecordingPermission,
    checkRecordingCapability,
    stopAudioRecording,
    writeToFileSystem,
    fileSystemLoadFiles,
    getFileSystemFile,
    deleteFileSystemFile,
    downloadFile,
    checkFinePickerPermission,
    pickAnyFile,
    pickVideos,
    pickImages,
    pickMedia,
    pickDirectory,
    processFilePickerFiles,
    getPickedFilesPath,
    openFileWithFileOpener,
    setDocumentsUrl,
    readFileBlob,
    blobTobase64,
    setAttachment,
    getMsgType,
    resetAttachment,
    onPaste,
    onChange,
    dragover,
    dragleave,
    drop,
    attachFileOpenActions,
    createFile,
    uploadFile,
    removeDoc,
    deleteAttachment,
    editImage,
    editPhoto,
    viewOtherFiles,
    openDocument,
    getFileMimeType,
    generateIconHtml,
    addVideoUrls,
    addVideo,
    removeVideo,
    saveVideos,
  };
}
